<script setup lang="ts">
import { toRef } from "vue";
import { useField } from "vee-validate";
const props = defineProps({
  type: {
    type: String,
    default: "text",
  },
  value: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  options: {
    type: Array,
    default: () => [],
  },
  isRequired: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  preEnteredEmail: {
    default: null,
    type: String,
  },
});

const emit = defineEmits(["focus"]);
// use `toRef` to create reactive references to `name` prop which is passed to `useField`
// this is important because vee-validte needs to know if the field name changes
// https://vee-validate.logaretm.com/v4/guide/composition-api/caveats
const name = toRef(props, "name");

// we don't provide any rules here because we are using form-level validation
// https://vee-validate.logaretm.com/v4/guide/validation#form-level-validation
const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  meta,
} = useField(name, undefined, {
  initialValue: props.value,
});

function whatClass() {
  if (meta.touched && meta.valid) {
    return "is-success";
  } else if (meta.touched && !meta.valid) {
    return "is-danger";
  } else {
    return "";
  }
}
</script>

<template>
  <span>
    <div class="control has-icons-right">
      <input
        :class="`input ${whatClass()}`"
        :name="name"
        :id="name"
        :type="type"
        v-bind="
          type !== 'file'
            ? {
                value:
                  props.preEnteredEmail && type === 'email'
                    ? props.preEnteredEmail
                    : inputValue,
              }
            : {}
        "
        :placeholder="placeholder"
        :disabled="disabled"
        :aria-label="label"
        @input="handleChange"
        @blur="handleBlur"
        @focus="$emit('focus')"
      />
      <span v-if="meta.valid" class="icon is-small is-right has-text-success">
        <i class="mdi mdi-check"></i>
      </span>
      <span
        v-else-if="meta.touched && !meta.valid"
        class="icon is-small is-right has-text-danger"
      >
        <i class="mdi mdi-alert"></i>
      </span>

      <p
        :class="`is-size-7 ${
          errorMessage ? 'has-text-danger' : 'has-text-success'
        }`"
        v-show="errorMessage || meta.valid"
      >
        {{ errorMessage }}
      </p>
    </div>
  </span>
</template>

<style scoped>
.TextInput {
  position: relative;
  margin-bottom: calc(1em * 1.5);
  width: 100%;
}

label {
  display: block;
  margin-bottom: 4px;
  width: 100%;
}

input {
  border-radius: 5px;
  border: 2px solid transparent;
  padding: 15px 10px;
  outline: none;
  background-color: #f2f5f7;
  width: 100%;
  transition: border-color 0.3s ease-in-out, color 0.3s ease-in-out,
    background-color 0.3s ease-in-out;
}

input:focus {
  border-color: var(--primary-color);
}

.help-message {
  position: absolute;
  bottom: calc(-1.5 * 1em);
  left: 0;
  margin: 0;
  font-size: 14px;
}

.TextInput.has-error input {
  background-color: var(--error-bg-color);
  color: var(--error-color);
}

.TextInput.has-error input:focus {
  border-color: var(--error-color);
}

.TextInput.has-error .help-message {
  color: var(--error-color);
}

.TextInput.success input {
  background-color: var(--success-bg-color);
  color: var(--success-color);
}

.TextInput.success input:focus {
  border-color: var(--success-color);
}

.TextInput.success .help-message {
  color: var(--success-color);
}
</style>
